import { useState, useEffect } from "react";
import { useContent } from "@/context/ContentContext";
import { Plus, Pencil, Trash2, Save, X, Upload } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ICONOS = ["Waves", "Wind", "Bike", "Map", "Zap", "Leaf", "Compass", "Mountain", "Star", "Heart"];

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

const EMPTY = { id: "", nombre: "", descripcion: "", icono: "Zap", color: "#0072A4", imagen: "" };

export default function ActivitiesManager() {
  const { content, updateSection } = useContent();
  const [activities, setActivities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (content.activities) setActivities([...content.activities]);
  }, [content.activities]);

  const save = async (list) => {
    setSaving(true);
    try { await updateSection("activities", list); setActivities(list); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleEdit = (act) => { setEditing(act.id); setForm({ ...act }); };
  const handleAdd = () => { setEditing("__new__"); setForm({ ...EMPTY, id: Date.now().toString() }); };
  const handleCancel = () => { setEditing(null); setForm(EMPTY); };

  const handleSave = async () => {
    let updated;
    if (editing === "__new__") {
      updated = [...activities, { ...form }];
    } else {
      updated = activities.map(a => a.id === editing ? { ...form } : a);
    }
    await save(updated);
    setEditing(null);
    setForm(EMPTY);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta actividad?")) return;
    await save(activities.filter(a => a.id !== id));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/media/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      setForm(p => ({ ...p, imagen: url }));
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="activities-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Actividades</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Gestiona las actividades de la landing page</p>
        </div>
        <button onClick={handleAdd} data-testid="add-activity-btn"
          className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-semibold px-4 py-2 rounded-lg hover:bg-[#005A80] transition-all text-sm">
          <Plus size={16} /> Añadir actividad
        </button>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-white rounded-xl border-2 border-[#0072A4] p-6 mb-6" data-testid="activity-form">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">{editing === "__new__" ? "Nueva actividad" : "Editar actividad"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">ID único</label>
              <input value={form.id} onChange={e => setForm(p => ({ ...p, id: e.target.value }))} data-testid="activity-id"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="kayak, btt, paddle..." />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Nombre</label>
              <input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} data-testid="activity-nombre"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Kayak" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Descripción</label>
              <textarea value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} rows={2} data-testid="activity-descripcion"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] resize-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Icono</label>
              <select value={form.icono} onChange={e => setForm(p => ({ ...p, icono: e.target.value }))} data-testid="activity-icono"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]">
                {ICONOS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Color</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} data-testid="activity-color"
                  className="w-10 h-10 rounded-lg border border-[#E2E8F0] cursor-pointer" />
                <input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                  className="flex-1 border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Imagen (URL)</label>
              <input value={form.imagen} onChange={e => setForm(p => ({ ...p, imagen: e.target.value }))} data-testid="activity-imagen"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="https://..." />
              <div className="mt-2 flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer bg-[#F0F8FF] px-3 py-2 rounded-lg text-sm text-[#0072A4] font-['Montserrat'] font-semibold hover:bg-[#E0F0FF] transition-colors">
                  <Upload size={14} /> Subir imagen
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="activity-image-upload" />
                </label>
                {uploading && <span className="text-xs text-[#94A3B8] font-['Montserrat']">Subiendo...</span>}
              </div>
              {form.imagen && <img src={getImageUrl(form.imagen)} alt="preview" className="mt-2 h-24 w-full object-cover rounded-lg border border-[#E2E8F0]" onError={e => e.target.style.display='none'} />}
            </div>
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-[#E2E8F0]">
            <button onClick={handleSave} disabled={saving} data-testid="save-activity-btn"
              className="flex items-center gap-2 bg-[#3CB44B] text-white font-['Montserrat'] font-semibold px-5 py-2 rounded-lg hover:bg-[#2E9B3A] transition-all text-sm disabled:opacity-60">
              <Save size={15} /> {saving ? "Guardando..." : "Guardar"}
            </button>
            <button onClick={handleCancel} data-testid="cancel-activity-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] font-['Montserrat'] text-sm hover:bg-[#F8FAFC] transition-colors">
              <X size={15} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {activities.map((act) => (
          <div key={act.id} data-testid={`activity-item-${act.id}`}
            className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden hover:shadow-md transition-shadow">
            {act.imagen && (
              <img src={getImageUrl(act.imagen)} alt={act.nombre} className="w-full h-28 object-cover" onError={e => e.target.style.display='none'} />
            )}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full" style={{ background: act.color }} />
                <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-sm">{act.nombre}</h3>
              </div>
              <p className="text-[#475569] font-['Montserrat'] text-xs line-clamp-2 mb-3">{act.descripcion}</p>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(act)} data-testid={`edit-activity-${act.id}`}
                  className="flex items-center gap-1 text-[#0072A4] font-['Montserrat'] text-xs font-semibold hover:text-[#005A80] transition-colors">
                  <Pencil size={12} /> Editar
                </button>
                <button onClick={() => handleDelete(act.id)} data-testid={`delete-activity-${act.id}`}
                  className="flex items-center gap-1 text-red-500 font-['Montserrat'] text-xs font-semibold hover:text-red-700 transition-colors ml-auto">
                  <Trash2 size={12} /> Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
